import { FunctionComponent, memo } from "react";
import { IProps, ITagsProportionBars } from "./anomaly-tags-proportion-bars.types";
import { useTagsProportionBarsData } from "./anomaly-tags-proportion-bars.utilities";
import AnomalyProportionBar from "../anomaly-proportion-bars/anomaly-proportion-bar";
import { SpinnerUntil } from "@edgetier/components";
import { BoxContent } from "@edgetier/client-components";
import "./anomaly-tags-proportion-bars.scss";

/**
 * For a number of items, show how many interactions match or contain that item (in total and as a percentage of the
 * total number of interactions).
 * @param props.anomalyId            Current anomaly id
 * @param props.anomalyStartDateTime Current anomaly start date time
 * @param props.anomalyEndDateTime   Current anomaly end date time
 * @param props.selectedTagGroup     Selected tag group
 * @param props.seletedItems         Currently selected items
 * @param props.onToggle             Callback when a user clicks one of the items
 */
const AnomalyTagsProportionBars: FunctionComponent<IProps> = ({
    anomalyId,
    anomalyStartDateTime,
    anomalyEndDateTime,
    selectedTagGroup,
    selectedItems,
    onToggle,
}) => {
    const { tagProportionsBars, isLoading } = useTagsProportionBarsData({
        anomalyId,
        anomalyEndDateTime,
        anomalyStartDateTime,
        groupBy: "tag_id",
        tagGroupId: selectedTagGroup.tagGroupId,
    });

    return (
        <SpinnerUntil<ITagsProportionBars[]> data={tagProportionsBars} isReady={!isLoading}>
            {(tagProportionsBars) =>
                !Array.isArray(tagProportionsBars) ? (
                    <BoxContent>Error</BoxContent>
                ) : (
                    <BoxContent>
                        {tagProportionsBars.length === 0 && <div>No tags found in this group</div>}
                        {tagProportionsBars.length !== 0 &&
                            tagProportionsBars.every(
                                (tag) => tag.tagProportionValue === 0 && tag.tagProportionPriorDateValue === 0
                            ) && <div>There are no tag changes in this group.</div>}
                        {tagProportionsBars.map((tag) => {
                            return (
                                (tag.tagProportionValue !== 0 || tag.tagProportionPriorDateValue !== 0) && (
                                    <AnomalyProportionBar
                                        key={tag.tagId}
                                        label={{ id: tag.tagId, label: tag.tag }}
                                        onToggle={onToggle}
                                        proportion={tag.tagProportionValue}
                                        proportionChange={tag.tagProportionBarChange}
                                        isSelected={selectedItems.includes(tag.tagId)}
                                        interactionsCount={tag.tagInteractionCount}
                                    />
                                )
                            );
                        })}
                    </BoxContent>
                )
            }
        </SpinnerUntil>
    );
};

export default memo(AnomalyTagsProportionBars);
